import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Table, Button, Card, Space, Tag, Typography, message, Popconfirm, Modal, Form, Input, Result } from 'antd'
import { PlusOutlined, LinkOutlined, CopyOutlined } from '@ant-design/icons'
import { getProducts, archiveProduct } from '../../services/product'
import { createCheckoutSession } from '../../services/checkout'
import { formatTimestamp } from '../../utils/format'
import type { Product, Pagination } from '../../types'

const { Title, Text } = Typography

export default function ProductList() {
  const [products, setProducts] = useState<Product[]>([])
  const [pagination, setPagination] = useState<Pagination>({ page: 1, size: 20, total: 0, totalPages: 0 })
  const [loading, setLoading] = useState(true)
  const [pageSize, setPageSize] = useState(20)
  const navigate = useNavigate()

  // Checkout 連結生成相關狀態
  const [linkModalVisible, setLinkModalVisible] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [generatingLink, setGeneratingLink] = useState(false)
  const [generatedLink, setGeneratedLink] = useState<string | null>(null)
  const [form] = Form.useForm()

  const fetchProducts = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    getProducts({ page, size })
      .then((res) => {
        setProducts(res.data.items)
        setPagination(res.data.pagination)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  const handleArchive = async (id: string) => {
    try {
      await archiveProduct(id)
      message.success('產品已歸檔')
      fetchProducts(pagination.page)
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '操作失敗')
    }
  }

  const handleOpenLinkModal = (product: Product) => {
    setSelectedProduct(product)
    setGeneratedLink(null)
    form.resetFields()
    setLinkModalVisible(true)
  }

  const handleGenerateLink = async () => {
    try {
      const values = await form.validateFields()
      setGeneratingLink(true)
      const res = await createCheckoutSession({
        productId: selectedProduct!.id,
        successUrl: values.successUrl,
        cancelUrl: values.cancelUrl || undefined
      })
      setGeneratedLink(res.data.url)
      message.success('Checkout 連結已生成')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '生成連結失敗')
    } finally {
      setGeneratingLink(false)
    }
  }

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink)
      message.success('連結已複製到剪貼板')
    }
  }

  const handleCloseLinkModal = () => {
    setLinkModalVisible(false)
    setSelectedProduct(null)
    setGeneratedLink(null)
  }

  const formatAmount = (amount: string | number) => Number(amount).toFixed(2)

  const columns = [
    {
      title: '產品 ID',
      dataIndex: 'id',
      key: 'id',
      width: 240,
      ellipsis: true,
      render: (id: string) => (
        <Text copyable={{ text: id }} style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {id}
        </Text>
      ),
    },
    { title: '產品名稱', dataIndex: 'name', key: 'name', ellipsis: true },
    {
      title: '價格',
      key: 'price',
      render: (_: unknown, record: Product) => `${formatAmount(record.price)} ${record.currency}`
    },
    {
      title: '計費類型',
      key: 'type',
      render: (_: unknown, record: Product) => (
        <Tag color={record.productType === 'SUBSCRIPTION' ? 'blue' : 'default'}>
          {record.productType === 'SUBSCRIPTION' ? '訂閱' : '一次性付款'}
        </Tag>
      )
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'ACTIVE' ? 'green' : 'default'}>
          {status === 'ACTIVE' ? '在售' : '已歸檔'}
        </Tag>
      )
    },
    {
      title: '創建時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (ts: number) => formatTimestamp(ts)
    },
    {
      title: '操作',
      key: 'actions',
      width: 340,
      render: (_: unknown, record: Product) => (
        <Space size="small">
          {record.status === 'ACTIVE' && record.productType === 'ONE_TIME' && (
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => handleOpenLinkModal(record)}
              data-testid="generate-link-btn"
            >
              生成連結
            </Button>
          )}
          <Button type="link" size="small" onClick={() => navigate(`/dashboard/products/${record.id}`)}>編輯</Button>
          {record.status === 'ACTIVE' && (
            <Popconfirm title="確定要歸檔此產品嗎？" onConfirm={() => handleArchive(record.id)}>
              <Button type="link" size="small" danger>歸檔</Button>
            </Popconfirm>
          )}
        </Space>
      )
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>產品管理</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/dashboard/products/new')}
          data-testid="create-product-btn"
        >
          創建產品
        </Button>
      </div>
      <Card>
        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.size,
            total: pagination.total,
            onChange: (page, size) => {
              if (size !== pageSize) {
                setPageSize(size)
                fetchProducts(1, size)
              } else {
                fetchProducts(page, size)
              }
            },
          }}
        />
      </Card>

      {/* Checkout 連結生成 Modal */}
      <Modal
        title={generatedLink ? 'Checkout 連結已生成' : '生成 Checkout 連結'}
        open={linkModalVisible}
        onCancel={handleCloseLinkModal}
        footer={generatedLink ? [
          <Button key="close" onClick={handleCloseLinkModal}>完成</Button>
        ] : [
          <Button key="cancel" onClick={handleCloseLinkModal}>取消</Button>,
          <Button key="generate" type="primary" loading={generatingLink} onClick={handleGenerateLink}>
            生成連結
          </Button>
        ]}
      >
        {generatedLink ? (
          <Result
            status="success"
            title="連結已生成"
            subTitle="連結有效期：24 小時"
            extra={
              <Space direction="vertical" style={{ width: '100%' }}>
                <Input.Group compact style={{ display: 'flex' }}>
                  <Input
                    value={generatedLink}
                    readOnly
                    style={{ fontFamily: 'monospace', flex: 1 }}
                  />
                  <Button icon={<CopyOutlined />} onClick={handleCopyLink}>複製</Button>
                </Input.Group>
              </Space>
            }
          />
        ) : (
          <>
            {selectedProduct && (
              <div style={{
                marginBottom: 16,
                padding: '12px 16px',
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                borderRadius: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Text strong style={{ color: 'var(--landing-text-primary)' }}>{selectedProduct.name}</Text>
                <Text style={{ color: 'var(--landing-accent-cyan)', fontWeight: 600, fontSize: 16 }}>
                  {formatAmount(selectedProduct.price)} {selectedProduct.currency}
                </Text>
              </div>
            )}
            <Form form={form} layout="vertical">
              <Form.Item
                name="successUrl"
                label="成功跳轉 URL"
                rules={[
                  { required: true, message: '請輸入成功跳轉 URL' },
                  { pattern: /^https?:\/\//, message: '請輸入有效的 URL' }
                ]}
              >
                <Input placeholder="https://example.com/success" />
              </Form.Item>
              <Form.Item
                name="cancelUrl"
                label="取消跳轉 URL（可選）"
                rules={[
                  { pattern: /^https?:\/\//, message: '請輸入有效的 URL' }
                ]}
              >
                <Input placeholder="https://example.com/cancel" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </div>
  )
}
